import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useAcademicYear } from '../hooks/useAcademicYear';
import {
  Table,
  Button,
  Form,
  InputGroup,
  Badge,
  Spinner,
  Alert,
  Modal,
  Dropdown,
} from 'react-bootstrap';
import {
  FaPlus,
  FaCamera,
  FaIdBadge,
  FaInfoCircle,
  FaFileExcel,
  FaPrint,
  FaUserGraduate,
  FaSearch,
  FaEllipsisV,
  FaCompress,
  FaExpand,
  FaChevronLeft,
  FaChevronRight,
  FaCheckSquare,
  FaSquare,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const StudentListV2 = () => {
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedAcademicYear } = useAppContext();
  const { academicYears } = useAcademicYear();
  const [student, setStudent] = useState(null);
  const [idBg, setIdBg] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [batches, setBatches] = useState([]);
  const [assignYearBatches, setAssignYearBatches] = useState([]);
  const [selectedBatchAcademicYearId, setSelectedBatchAcademicYearId] =
    useState(selectedAcademicYear?.id || '');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

  // ✅ For selection
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [bulkActionMode, setBulkActionMode] = useState('batch');
  const [selectedStudentStatus, setSelectedStudentStatus] = useState('');
  const [compactView, setCompactView] = useState(true);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoToShow, setPhotoToShow] = useState('');
  const [showPhotosInline, setShowPhotosInline] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [showHintAnimation, setShowHintAnimation] = useState(false);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({
    serialNo: true,
    admissionNo: true,
    applicationNo: true,
    name: true,
    mobile: true,
    status: true,
    batches: true,
    address: false,
    gender: false,
    dob: false,
    parents: false,
    dateOfApplication: false,
    dateOfAdmission: false,
    idCardNo: false,
    parentAccessCode: false,
    remark: false,
  });

  // ✅ Initialize state from URL or default values
  const [limit, setLimit] = useState(Number(searchParams.get('limit')) || 100);
  const [offset, setOffset] = useState(Number(searchParams.get('offset')) || 0);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [batch, setBatch] = useState(searchParams.get('batch') || '');
  const [status, setStatus] = useState(searchParams.get('status') || 'Active');
  const [admitStatus, setAdmitStatus] = useState(
    searchParams.get('admit') || 'Admitted',
  );

  // ✅ Fetch students & batches when filters change
  useEffect(() => {
    fetchStudents();
    fetchBatches();
  }, [limit, offset, search, batch, status, admitStatus, selectedAcademicYear]);

  // ✅ Handle window resize to adjust filters state
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 576;
      if (!isMobile && filtersCollapsed) {
        setFiltersCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [filtersCollapsed]);

  // ✅ Sync URL params whenever filters change
  useEffect(() => {
    const params = {
      search,
      batch,
      status,
      admit: admitStatus,
      limit,
      offset,
    };

    Object.keys(params).forEach((key) => {
      if (!params[key]) delete params[key];
    });

    setSearchParams(params);
  }, [search, batch, status, admitStatus, limit, offset, setSearchParams]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && student && showModal) {
      const studentData = {
        ...student,
        photo_path: student.photo
          ? `${appConstants.API_ROOT_URL}uploads/students/${student.photo}`
          : 'default.png',
        batch_detail: student.batch_info,
        id_bg_path: `${appConstants.API_ROOT_URL}uploads/${idBg}`,
      };

      const timeoutId = setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.contentWindow.postMessage(studentData, '*');
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [student, showModal, idBg]);

  useEffect(() => {
    setSelectAll(
      students.length > 0 && selectedStudents.length === students.length,
    );
  }, [selectedStudents, students]);

  const fetchStudents = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }
    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setStudents([]);
      setTotal(0);
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}list-students-v2`,
        {
          limit,
          offset,
          search,
          batch: batch || null,
          status: status !== 'All' ? status : null,
          admit_status: admitStatus,
          academic_year_id: selectedAcademicYear.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setIdBg(response.data.id_bg);
      setStudents(response.data.data || []);
      setTotal(response.data.total || 0);
      setSelectedStudents([]); // reset selections
      setSelectAll(false);
    } catch (error) {
      console.error('Error fetching students:', error);
      setAlert({
        show: true,
        message: 'Error loading students. Please try again.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    const token = getAuthToken();
    if (!token) return;
    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setBatches([]);
      return;
    }
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-batches-v3`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );
      setBatches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
      setBatches([]);
    }
  };

  useEffect(() => {
    if (selectedAcademicYear && selectedAcademicYear.id) {
      setSelectedBatchAcademicYearId(selectedAcademicYear.id);
    }
  }, [selectedAcademicYear]);

  useEffect(() => {
    const fetchAssignYearBatches = async () => {
      const token = getAuthToken();
      if (!token || !selectedBatchAcademicYearId) {
        setAssignYearBatches([]);
        setSelectedBatch('');
        return;
      }

      try {
        const response = await axios.get(
          `${appConstants.API_ROOT_URL}get-batches-v3`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { academic_year_id: selectedBatchAcademicYearId },
          },
        );
        setAssignYearBatches(response.data.data || []);
      } catch (error) {
        console.error('Error fetching batches for selected year:', error);
        setAssignYearBatches([]);
      }
      setSelectedBatch('');
    };

    fetchAssignYearBatches();
  }, [selectedBatchAcademicYearId]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setOffset(0);
  };

  const handleBatchChange = (e) => {
    setBatch(e.target.value);
    setOffset(0);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setOffset(0);
  };

  const handleAdmitStatusChange = (e) => {
    setAdmitStatus(e.target.value);
    setOffset(0);
  };

  const handleNext = () => {
    if (offset + limit < total) setOffset(offset + limit);
  };

  const handlePrev = () => {
    if (offset - limit >= 0) setOffset(offset - limit);
  };

  const handleIdClick = (thisStudent) => {
    setStudent(thisStudent);
    setShowModal(true);
  };

  const handleAdmit = async (application_no, studentIndex) => {
    if (!window.confirm('Are you sure you want to admit this student?')) return;

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}admit-student`,
        { application_no },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: `Student admitted! Admission No: ${response.data.admission_no}`,
          variant: 'success',
        });
        setTimeout(
          () => setAlert({ show: false, message: '', variant: '' }),
          3000,
        );
        setStudents((prev) => {
          const updated = [...prev];
          updated[studentIndex].admission_no = response.data.admission_no;
          return updated;
        });
      } else {
        setAlert({
          show: true,
          message: response.data.message,
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message: err.response?.data?.message || 'Error admitting student.',
        variant: 'danger',
      });
    }
  };

  const handleViewSummary = (id) => navigate(`/student-summary/${id}`);
  const handlePrintSummary = (id) =>
    window.open(`/instv2/student-summary-printable/${id}`, '_blank');

  const handleResetFilters = () => {
    setSearch('');
    setBatch('');
    setStatus('Active');
    setAdmitStatus('Admitted');
    setOffset(0);
  };

  const downloadExcel = () => {
    if (!students || students.length === 0) {
      setAlert({
        show: true,
        message: 'No students to download',
        variant: 'warning',
      });
      return;
    }

    // Build headers based on selected columns
    const columnMapping = {
      serialNo: 'S.No',
      admissionNo: 'Admission No',
      applicationNo: 'Application No',
      name: 'Name',
      mobile: 'Mobile',
      status: 'Status',
      batches: 'Batches',
      address: 'Address',
      gender: 'Gender',
      dob: 'Date of Birth',
      parents: 'Parents',
      dateOfApplication: 'Application Date',
      dateOfAdmission: 'Admission Date',
      idCardNo: 'ID Card No',
      parentAccessCode: 'Parent Access Code',
      remark: 'Remarks',
    };

    const headers = [];
    const selectedColumnKeys = [];

    Object.keys(selectedColumns).forEach((key) => {
      if (selectedColumns[key]) {
        headers.push(columnMapping[key]);
        selectedColumnKeys.push(key);
      }
    });

    const rows = students.map((s, idx) => {
      const row = [];

      selectedColumnKeys.forEach((key) => {
        switch (key) {
          case 'serialNo':
            row.push(idx + 1);
            break;
          case 'admissionNo':
            row.push(s.admission_no ? s.admission_no.toString() : '');
            break;
          case 'applicationNo':
            row.push(s.application_no ? s.application_no.toString() : '');
            break;
          case 'name':
            row.push(s.name || '');
            break;
          case 'mobile':
            const mobileRaw = (s.mobile || '').toString().replace(/\n/g, ' ');
            row.push(mobileRaw ? `'${mobileRaw}` : '');
            break;
          case 'status':
            row.push(s.status || '');
            break;
          case 'batches':
            const batches =
              s.batches && s.batches.length
                ? s.batches
                    .map((b) => `${b.class_name} ${b.division}`)
                    .join('; ')
                : '';
            row.push(batches);
            break;
          case 'address':
            row.push(s.address || '');
            break;
          case 'gender':
            row.push(s.gender || '');
            break;
          case 'dob':
            row.push(s.dob && s.dob !== '0000-00-00' ? s.dob : '');
            break;
          case 'parents':
            row.push(s.parents || '');
            break;
          case 'dateOfApplication':
            row.push(s.date_of_application || '');
            break;
          case 'dateOfAdmission':
            row.push(s.date_of_admission || '');
            break;
          case 'idCardNo':
            row.push(s.id_card_no || '');
            break;
          case 'parentAccessCode':
            row.push(s.parent_access_code || '');
            break;
          case 'remark':
            row.push(s.remark || '');
            break;
          default:
            row.push('');
        }
      });

      return row;
    });

    const csv = [headers, ...rows]
      .map((r) => r.map((f) => `"${(f + '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `students_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleColumnToggle = (column) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const selectAllColumns = () => {
    const allSelected = {};
    Object.keys(selectedColumns).forEach((key) => {
      allSelected[key] = true;
    });
    setSelectedColumns(allSelected);
  };

  const deselectAllColumns = () => {
    const allDeselected = {};
    Object.keys(selectedColumns).forEach((key) => {
      allDeselected[key] = false;
    });
    setSelectedColumns(allDeselected);
  };

  const showTimedAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 3000);
  };

  const handleBulkActionModeChange = (mode) => {
    setBulkActionMode(mode);
    if (mode === 'batch') {
      setSelectedStudentStatus('');
      return;
    }

    setSelectedBatch('');
  };

  const buildStudentUpdateFormData = (studentData, overrides = {}) => {
    const formDataToSend = new FormData();

    Object.entries(studentData).forEach(([key, value]) => {
      if (key !== 'batches') {
        formDataToSend.append(key, value ?? '');
      }
    });

    formDataToSend.delete('status');
    formDataToSend.append(
      'status',
      overrides.status ?? studentData.status ?? '',
    );
    formDataToSend.delete('academic_year_id');
    formDataToSend.append('academic_year_id', selectedAcademicYear.id);

    (studentData.batches || []).forEach((batchItem) => {
      const batchId = batchItem.batch_id ?? batchItem.id;
      if (batchId) {
        formDataToSend.append('batch_ids[]', String(batchId));
      }
    });

    return formDataToSend;
  };

  const updateStudentStatus = async (studentId, nextStatus, token) => {
    const detailResponse = await axios.post(
      `${appConstants.API_ROOT_URL}get-student-v2`,
      {
        id: studentId,
        academic_year_id: selectedAcademicYear.id,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    if (detailResponse.data.status !== 'success' || !detailResponse.data.data) {
      throw new Error(
        detailResponse.data.message || 'Failed to fetch student details.',
      );
    }

    const studentData = detailResponse.data.data;
    const formDataToSend = buildStudentUpdateFormData(studentData, {
      status: nextStatus,
    });

    const updateResponse = await axios.post(
      `${appConstants.API_ROOT_URL}update-student-v3`,
      formDataToSend,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    if (updateResponse.data.status !== 'success') {
      throw new Error(
        updateResponse.data.message || 'Failed to update student status.',
      );
    }
  };

  // ✅ Handle selection
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map((s) => s.id));
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedStudents.includes(id)) {
      setSelectedStudents(selectedStudents.filter((sid) => sid !== id));
    } else {
      setSelectedStudents([...selectedStudents, id]);
    }
  };

  // ✅ Assign Batch for selected students
  const handleAssignBatch = async () => {
    if (selectedStudents.length === 0) {
      setAlert({
        show: true,
        message: 'Please select at least one student.',
        variant: 'warning',
      });
      return;
    }
    if (!selectedBatchAcademicYearId) {
      setAlert({
        show: true,
        message: 'Please select the academic year for the target batch.',
        variant: 'warning',
      });
      return;
    }
    if (!selectedBatch) {
      setAlert({
        show: true,
        message: 'Please select a batch to assign.',
        variant: 'warning',
      });
      return;
    }

    if (
      !window.confirm(
        `Assign selected ${selectedStudents.length} students to this batch?`,
      )
    )
      return;

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}assign-batch-to-students`,
        {
          student_ids: selectedStudents,
          batch_id: selectedBatch,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        showTimedAlert('Batch assigned successfully!');
        fetchStudents(); // refresh
      } else {
        setAlert({
          show: true,
          message: response.data.message || 'Something went wrong.',
          variant: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: error.response?.data?.message || 'Error assigning batch.',
        variant: 'danger',
      });
    }
  };

  // ✅ Remove Batch for selected students
  const handleRemoveBatch = async () => {
    if (selectedStudents.length === 0) {
      setAlert({
        show: true,
        message: 'Please select at least one student.',
        variant: 'warning',
      });
      return;
    }
    if (!selectedBatchAcademicYearId) {
      setAlert({
        show: true,
        message: 'Please select the academic year for the target batch.',
        variant: 'warning',
      });
      return;
    }
    if (!selectedBatch) {
      setAlert({
        show: true,
        message: 'Please select a batch.',
        variant: 'warning',
      });
      return;
    }

    if (
      !window.confirm(
        `Remove selected ${selectedStudents.length} students from this batch?`,
      )
    )
      return;

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}remove-batch-from-students`,
        {
          student_ids: selectedStudents,
          batch_id: selectedBatch,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        showTimedAlert(response.data.message || 'Batch removed successfully!');
        fetchStudents(); // refresh list
      } else {
        setAlert({
          show: true,
          message: response.data.message || 'Something went wrong.',
          variant: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: error.response?.data?.message || 'Error removing batch.',
        variant: 'danger',
      });
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedStudents.length === 0) {
      setAlert({
        show: true,
        message: 'Please select at least one student.',
        variant: 'warning',
      });
      return;
    }

    if (!selectedStudentStatus) {
      setAlert({
        show: true,
        message: 'Please select a status.',
        variant: 'warning',
      });
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setAlert({
        show: true,
        message: 'Please select an academic year.',
        variant: 'warning',
      });
      return;
    }

    if (
      !window.confirm(
        `Change status to ${selectedStudentStatus} for ${selectedStudents.length} selected students? Existing batch assignments will be kept unchanged.`,
      )
    ) {
      return;
    }

    setLoading(true);

    try {
      const results = await Promise.allSettled(
        selectedStudents.map((studentId) =>
          updateStudentStatus(studentId, selectedStudentStatus, token),
        ),
      );

      const successCount = results.filter(
        (result) => result.status === 'fulfilled',
      ).length;
      const failedResults = results.filter(
        (result) => result.status === 'rejected',
      );

      if (successCount > 0) {
        showTimedAlert(
          failedResults.length === 0
            ? `Updated status to ${selectedStudentStatus} for ${successCount} students.`
            : `Updated ${successCount} students. ${failedResults.length} failed.`,
          failedResults.length === 0 ? 'success' : 'warning',
        );
        setSelectedStudentStatus('');
        await fetchStudents();
      } else {
        setAlert({
          show: true,
          message:
            failedResults[0]?.reason?.message ||
            'Failed to update selected students.',
          variant: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message:
          error.response?.data?.message || 'Error updating student status.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '12px',
        }}
      >
        {/* Merged compact dark bar: filters (left) + icon buttons (right) */}
        <div
          className="compact-top-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '12px',
            backgroundColor: '#343a40',
            padding: '6px',
            borderRadius: '6px',
            color: 'white',
            flexWrap: 'wrap',
          }}
        >
          <div
            className={`compact-top-left ${filtersCollapsed ? 'collapsed' : ''}`}
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {!filtersCollapsed && (
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <InputGroup style={{ width: '220px' }}>
                    <InputGroup.Text
                      style={{
                        padding: '6px 8px',
                        background: 'transparent',
                        border: 'none',
                        color: '#fff',
                      }}
                    >
                      <FaSearch style={{ color: '#fff' }} />
                    </InputGroup.Text>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Search"
                      value={search}
                      onChange={handleSearch}
                      style={{ height: '30px' }}
                    />
                  </InputGroup>

                  <Form.Select
                    size="sm"
                    value={batch}
                    onChange={handleBatchChange}
                    style={{ width: '160px' }}
                  >
                    <option value="">All Batches</option>
                    {batches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.class_name} {b.division}
                      </option>
                    ))}
                  </Form.Select>

                  <Form.Select
                    size="sm"
                    value={status}
                    onChange={handleStatusChange}
                    style={{ width: '110px' }}
                  >
                    <option value="All">All</option>
                    <option value="Active">Active</option>
                    <option value="Suspend">Suspend</option>
                  </Form.Select>

                  <Form.Select
                    size="sm"
                    value={admitStatus}
                    onChange={handleAdmitStatusChange}
                    style={{ width: '120px' }}
                  >
                    <option value="All">All</option>
                    <option value="Admitted">Admitted</option>
                    <option value="Applied">Applied</option>
                  </Form.Select>

                  <Form.Select
                    size="sm"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setOffset(0);
                    }}
                    style={{ width: '90px' }}
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                  </Form.Select>

                  <Button
                    variant={compactView ? 'light' : 'outline-light'}
                    size="sm"
                    onClick={() => setCompactView(!compactView)}
                    style={{
                      borderRadius: '6px',
                      width: '34px',
                      height: '34px',
                      padding: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Toggle compact row view"
                    aria-label="Toggle compact view"
                  >
                    {compactView ? <FaCompress /> : <FaExpand />}
                  </Button>

                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={handleResetFilters}
                    style={{ borderRadius: '6px' }}
                  >
                    Reset
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div
            className="compact-top-actions"
            style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
          >
            <Button
              className="filters-toggle-btn"
              variant="outline-light"
              size="sm"
              onClick={() => setFiltersCollapsed(!filtersCollapsed)}
              style={{
                borderRadius: '6px',
                width: '34px',
                height: '34px',
                padding: 0,
              }}
              title={filtersCollapsed ? 'Show filters' : 'Hide filters'}
            >
              {filtersCollapsed ? <FaChevronDown /> : <FaChevronUp />}
            </Button>

            <div
              className="action-right"
              style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
            >
              <Button
                variant="light"
                size="sm"
                onClick={() => navigate('/create-student')}
                aria-label="New Student"
                style={{
                  width: '34px',
                  height: '28px',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                }}
              >
                <FaPlus />
              </Button>

              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="light"
                  size="sm"
                  id="students-actions-dropdown-2"
                  style={{
                    width: '34px',
                    height: '28px',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                  }}
                >
                  <FaEllipsisV />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => navigate('/bulk-upload-students')}
                  >
                    <FaFileExcel style={{ marginRight: '8px' }} /> Upload Excel
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setShowColumnModal(true)}>
                    <FaFileExcel style={{ marginRight: '8px' }} /> Download
                    Excel
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    onClick={() => setShowPhotosInline(!showPhotosInline)}
                  >
                    {showPhotosInline ? (
                      <FaCheckSquare style={{ marginRight: '8px' }} />
                    ) : (
                      <span style={{ display: 'inline-block', width: 16 }} />
                    )}
                    Show Photos Inline
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>

        {/* Responsive styles for small screens */}
        <style>{`
          .filters-toggle-btn { display: none; align-items: center; justify-content: center; transition: all 0.3s ease; }
          .compact-top-left {
            transition: all 0.2s ease-in-out;
            overflow: hidden;
          }
          @media (max-width: 576px) {
            .filters-toggle-btn { display: inline-flex !important; }
            .compact-top-bar { padding: 8px; }
            .compact-top-left { flex-direction: column; align-items: stretch; gap: 6px; width: 100%; }
            .compact-top-left.collapsed {
              max-height: 0;
              opacity: 0;
              margin: 0;
              padding: 0;
              transform: translateY(-10px);
            }
            .compact-top-left:not(.collapsed) {
              max-height: 500px;
              opacity: 1;
              transform: translateY(0);
            }
            .compact-top-actions { width: 100%; display: flex; justify-content: space-between; align-items: center; }
            .compact-top-left .input-group, .compact-top-left .form-control, .compact-top-left select, .compact-top-left .btn { width: 100% !important; }
            .student-table td, .student-table th { font-size: 0.82rem; padding: 6px !important; }
            .student-table td:first-child, .student-table th:first-child { width: 64px !important; }
            .student-table td img { width: 28px !important; height: 28px !important; }
            .compact-bulk .form-select { width: 140px !important; }
            .compact-top-bar .btn[aria-label="New Student"] { width: 40px !important; }
          }
        `}</style>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Bulk Actions Card (compact) */}
        {selectedStudents.length > 0 && (
          <div
            className="compact-bulk"
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '10px',
              marginBottom: '12px',
              boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
              border: '1px solid #e6e9f7',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
              }}
            >
              <FaCheckSquare style={{ color: '#667eea' }} />
              <div
                style={{
                  fontWeight: 600,
                  color: '#495057',
                  fontSize: '0.95rem',
                }}
              >
                {selectedStudents.length} selected
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  marginLeft: 'auto',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant={
                    bulkActionMode === 'batch' ? 'primary' : 'outline-primary'
                  }
                  size="sm"
                  onClick={() => handleBulkActionModeChange('batch')}
                  style={{ borderRadius: '6px', fontWeight: 600 }}
                >
                  Batch
                </Button>
                <Button
                  variant={
                    bulkActionMode === 'status' ? 'warning' : 'outline-warning'
                  }
                  size="sm"
                  onClick={() => handleBulkActionModeChange('status')}
                  style={{ borderRadius: '6px', fontWeight: 600 }}
                >
                  Status
                </Button>
              </div>
            </div>

            <div
              style={{
                marginTop: '10px',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {bulkActionMode === 'batch' ? (
                <>
                  <Form.Select
                    value={selectedBatchAcademicYearId}
                    onChange={(e) =>
                      setSelectedBatchAcademicYearId(e.target.value)
                    }
                    size="sm"
                    style={{ width: '220px', borderRadius: '6px' }}
                  >
                    <option value="">Target academic year...</option>
                    {academicYears.map((year) => (
                      <option key={year.id} value={year.id}>
                        {year.name || year.start_year || year.year}
                      </option>
                    ))}
                  </Form.Select>

                  <Form.Select
                    value={selectedBatch}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                    size="sm"
                    style={{ width: '240px', borderRadius: '6px' }}
                  >
                    <option value="">
                      {selectedBatchAcademicYearId
                        ? 'Assign batch...'
                        : 'Select year first...'}
                    </option>
                    {assignYearBatches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.class_name} {b.division}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAssignBatch}
                    disabled={!selectedBatch}
                    style={{
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontWeight: 600,
                    }}
                  >
                    Assign
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleRemoveBatch}
                    disabled={!selectedBatch}
                    style={{
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontWeight: 600,
                    }}
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <>
                  <Form.Select
                    value={selectedStudentStatus}
                    onChange={(e) => setSelectedStudentStatus(e.target.value)}
                    size="sm"
                    style={{ width: '200px', borderRadius: '6px' }}
                  >
                    <option value="">Set status...</option>
                    <option value="Active">Active</option>
                    <option value="Suspend">Suspend</option>
                  </Form.Select>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={handleBulkStatusUpdate}
                    disabled={!selectedStudentStatus}
                    style={{
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontWeight: 600,
                    }}
                  >
                    Update Status
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Students Table Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            minHeight: '60vh',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spinner animation="border" variant="primary" />
              <p style={{ marginTop: '15px', color: '#6c757d' }}>
                Loading students...
              </p>
            </div>
          ) : students.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#6c757d',
              }}
            >
              <FaUserGraduate
                style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '15px' }}
              />
              <p>No students found</p>
              <Button
                variant="primary"
                onClick={() => navigate('/create-student')}
                style={{
                  marginTop: '10px',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '600',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                }}
              >
                <FaPlus /> Add First Student
              </Button>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <style>{`.student-table td, .student-table th { vertical-align: middle; }`}</style>
                <Table
                  hover
                  responsive
                  striped
                  className="student-table"
                  style={{ marginBottom: '8px' }}
                >
                  <thead
                    style={{
                      backgroundColor: '#e9ecef',
                      borderBottom: '3px solid #dee2e6',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          width: '40px',
                          padding: compactView ? '10px 8px' : '12px 10px',
                          position: 'relative',
                          whiteSpace: 'nowrap',
                          fontSize: '0.95rem',
                          fontWeight: '700',
                          color: '#343a40',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: 0,
                            width: '6px',
                            background: '#fff0',
                          }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={toggleSelectAll}
                            style={{
                              cursor: 'pointer',
                              width: '18px',
                              height: '18px',
                              transform: 'scale(1.2)',
                              marginLeft: '8px',
                            }}
                          />
                          <div
                            style={{ minWidth: '26px', marginLeft: '8px' }}
                          />
                        </div>
                      </th>
                      <th
                        style={{
                          padding: '12px 10px',
                          fontWeight: '700',
                          color: '#343a40',
                          width: '12%',
                          whiteSpace: 'nowrap',
                          fontSize: '0.95rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Adm / App No
                      </th>
                      <th
                        style={{
                          padding: '12px 10px',
                          fontWeight: '700',
                          color: '#343a40',
                          width: '18%',
                          whiteSpace: 'nowrap',
                          fontSize: '0.95rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Name
                      </th>
                      <th
                        style={{
                          padding: '12px 10px',
                          fontWeight: '700',
                          color: '#343a40',
                          width: '18%',
                          whiteSpace: 'nowrap',
                          fontSize: '0.95rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Batch(es)
                      </th>
                      <th
                        style={{
                          padding: '12px 10px',
                          fontWeight: '700',
                          color: '#343a40',
                          width: '14%',
                          whiteSpace: 'nowrap',
                          fontSize: '0.95rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Mobile
                      </th>
                      <th
                        style={{
                          padding: '12px 10px',
                          fontWeight: '700',
                          color: '#343a40',
                          width: '22%',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          fontSize: '0.95rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, index) => (
                      <tr
                        key={student.id}
                        onDoubleClick={() =>
                          navigate(`/student-view/${student.id}`)
                        }
                        style={{
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <td
                          style={{
                            padding: compactView ? '6px' : '12px',
                            width: '40px',
                            position: 'relative',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              bottom: 0,
                              left: 0,
                              width: '6px',
                              background:
                                student.status === 'Active'
                                  ? '#28a745'
                                  : '#dc3545',
                              borderRadius: '6px 0 0 6px',
                            }}
                          />
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              height: '100%',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedStudents.includes(student.id)}
                              onChange={() => toggleSelectOne(student.id)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                cursor: 'pointer',
                                width: '18px',
                                height: '18px',
                                transform: 'scale(1.2)',
                                marginLeft: '8px',
                              }}
                            />
                            <div
                              style={{
                                fontSize: '0.9rem',
                                color: '#495057',
                                marginLeft: '8px',
                                minWidth: '26px',
                                textAlign: 'right',
                              }}
                            >
                              {offset + index + 1}
                            </div>
                            {/* Photo indicator / thumbnail */}
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = student.photo
                                  ? `${appConstants.API_ROOT_URL}uploads/students/${student.photo}`
                                  : '';
                                if (url) {
                                  setPhotoToShow(url);
                                  setShowPhotoModal(true);
                                }
                              }}
                              title={student.photo ? 'View Photo' : 'No Photo'}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '10px',
                                cursor: student.photo ? 'pointer' : 'default',
                              }}
                            >
                              {showPhotosInline && student.photo ? (
                                <img
                                  src={`${appConstants.API_ROOT_URL}uploads/students/${student.photo}`}
                                  alt="student"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    border: '1px solid #e9ecef',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const url = `${appConstants.API_ROOT_URL}uploads/students/${student.photo}`;
                                    setPhotoToShow(url);
                                    setShowPhotoModal(true);
                                  }}
                                />
                              ) : (
                                <FaCamera
                                  style={{
                                    marginLeft: '6px',
                                    color: student.photo
                                      ? '#198754'
                                      : '#adb5bd',
                                  }}
                                />
                              )}
                            </span>
                            {/* ID card presence indicator (non-clickable) */}
                            <span
                              title={
                                student.id_card_no
                                  ? 'Has ID Card'
                                  : 'No ID Card'
                              }
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '8px',
                                cursor: 'default',
                                color: student.id_card_no
                                  ? '#198754'
                                  : '#adb5bd',
                              }}
                            >
                              <FaIdBadge />
                            </span>
                          </div>
                        </td>
                        {/* Serial number moved into first cell (left of checkbox) */}
                        <td style={{ padding: '12px', fontSize: '0.9rem' }}>
                          {student.admission_no ? (
                            <Badge
                              bg="success"
                              style={{
                                fontSize: '0.85rem',
                                padding: '6px 10px',
                              }}
                            >
                              {student.admission_no}
                            </Badge>
                          ) : (
                            <Badge
                              bg="secondary"
                              style={{
                                fontSize: '0.85rem',
                                padding: '6px 10px',
                              }}
                            >
                              App: {student.application_no}
                            </Badge>
                          )}
                        </td>
                        <td
                          style={{
                            padding: compactView ? '6px' : '12px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                          }}
                        >
                          {student.name}
                        </td>
                        <td
                          style={{
                            padding: compactView ? '6px' : '12px',
                            fontSize: '0.85rem',
                          }}
                        >
                          {student.batches && student.batches.length > 0 ? (
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                              }}
                            >
                              {student.batches.map((b, i) => (
                                <Badge
                                  key={i}
                                  bg="info"
                                  text="dark"
                                  style={{ fontSize: '0.75rem' }}
                                >
                                  {b.class_name} {b.division}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span
                              style={{ color: '#6c757d', fontStyle: 'italic' }}
                            >
                              No batch
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: compactView ? '6px' : '12px',
                            fontSize: '0.9rem',
                            wordBreak: 'break-word',
                            whiteSpace: compactView ? 'normal' : 'pre-wrap',
                            overflow: 'hidden',
                            lineHeight: '1.2em',
                            backgroundColor: 'transparent',
                          }}
                        >
                          {compactView
                            ? student.mobile || ''
                            : student.mobile
                              ? student.mobile.replace(/,/g, '\n')
                              : ''}
                        </td>

                        <td
                          style={{ padding: '8px 12px', textAlign: 'center' }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              gap: '6px',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            {!student.admission_no && (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAdmit(student.application_no, index);
                                }}
                                style={{
                                  borderRadius: '6px',
                                  width: '34px',
                                  height: '34px',
                                  padding: 0,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                                title="Admit Student"
                              >
                                <FaCheckSquare />
                              </Button>
                            )}

                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewSummary(student.id);
                              }}
                              title="View Summary"
                              style={{
                                borderRadius: '6px',
                                width: '34px',
                                height: '34px',
                                padding: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <FaInfoCircle />
                            </Button>

                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePrintSummary(student.id);
                              }}
                              title="Print Summary"
                              style={{
                                borderRadius: '6px',
                                width: '34px',
                                height: '34px',
                                padding: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <FaPrint />
                            </Button>

                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleIdClick(student);
                              }}
                              title="View ID Card"
                              style={{
                                borderRadius: '6px',
                                width: '34px',
                                height: '34px',
                                padding: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <FaIdBadge />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '20px',
                  flexWrap: 'wrap',
                  gap: '15px',
                }}
              >
                <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                  Showing {offset + 1} to {Math.min(offset + limit, total)} of{' '}
                  {total} students
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button
                    variant="outline-secondary"
                    onClick={handlePrev}
                    disabled={offset === 0}
                    style={{
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <FaChevronLeft /> Previous
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleNext}
                    disabled={offset + limit >= total}
                    style={{
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    Next <FaChevronRight />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal for ID card */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Modal.Title
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <FaIdBadge /> Student ID Card
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <iframe
            ref={iframeRef}
            src="/instv2/idcard/index.html?v=5"
            style={{
              width: '100%',
              height: '500px',
              border: 'none',
              borderRadius: '8px',
            }}
            title="ID Card"
          />
        </Modal.Body>
      </Modal>
      {/* Modal for viewing student photo */}
      <Modal
        show={showPhotoModal}
        onHide={() => setShowPhotoModal(false)}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Student Photo</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center' }}>
          {photoToShow ? (
            <img
              src={photoToShow}
              alt="Student"
              style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 8 }}
            />
          ) : (
            <div style={{ color: '#6c757d' }}>No photo available</div>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal for Column Selection */}
      <Modal
        show={showColumnModal}
        onHide={() => setShowColumnModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFileExcel style={{ marginRight: '8px' }} />
            Select Columns for Excel Export
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: '15px' }}>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={selectAllColumns}
              style={{ marginRight: '10px' }}
            >
              Select All
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={deselectAllColumns}
            >
              Deselect All
            </Button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '10px',
            }}
          >
            {Object.entries({
              serialNo: 'Serial Number',
              admissionNo: 'Admission Number',
              applicationNo: 'Application Number',
              name: 'Student Name',
              mobile: 'Mobile Number',
              status: 'Status',
              batches: 'Batches',
              address: 'Address',
              gender: 'Gender',
              dob: 'Date of Birth',
              parents: 'Parents',
              dateOfApplication: 'Application Date',
              dateOfAdmission: 'Admission Date',
              idCardNo: 'ID Card Number',
              parentAccessCode: 'Parent Access Code',
              remark: 'Remarks',
            }).map(([key, label]) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '5px',
                }}
              >
                <input
                  type="checkbox"
                  id={`col-${key}`}
                  checked={selectedColumns[key]}
                  onChange={() => handleColumnToggle(key)}
                  style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                />
                <label
                  htmlFor={`col-${key}`}
                  style={{
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: selectedColumns[key] ? '600' : 'normal',
                  }}
                >
                  {label}
                </label>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: '20px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
            }}
          >
            <small style={{ color: '#6c757d' }}>
              <strong>Selected:</strong>{' '}
              {Object.values(selectedColumns).filter(Boolean).length} columns
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowColumnModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              downloadExcel();
              setShowColumnModal(false);
            }}
            disabled={Object.values(selectedColumns).every((val) => !val)}
          >
            <FaFileExcel style={{ marginRight: '6px' }} />
            Download Excel
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default StudentListV2;
